/**
 * Error Sanitizer for Frontend
 *
 * Sanitizes backend error messages to safe, generic messages
 * Prevents exposing sensitive backend information in production
 *
 * In development: shows raw messages for debugging
 * In production: shows generic, user-friendly messages
 */

const isDevelopment = process.env.NODE_ENV === 'development';

// Map HTTP status codes to safe messages
const STATUS_CODE_MESSAGES = {
  400: 'Invalid request. Please check your input.',
  401: 'Your session has expired. Please log in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'This action conflicts with existing data.',
  422: 'Please check your input and try again.',
  429: 'Too many requests. Please wait a moment and try again.',
  500: 'An unexpected error occurred. Please try again later.',
  503: 'The service is temporarily unavailable. Please try again later.',
};

// Map common backend error patterns to safe messages
const ERROR_PATTERN_MAPPING = {
  // Database errors
  'duplicate|already exists|unique constraint': 'This record already exists.',
  'foreign key|references': 'Cannot delete this item because it has dependencies.',
  'not null|required field': 'Please provide all required fields.',

  // Authentication errors
  'invalid.*password|wrong password': 'Invalid email or password.',
  'email not found|user not found': 'Invalid email or password.',
  'token.*invalid|token.*expired': 'Your session has expired. Please log in again.',
  'unauthorized|forbidden': 'You do not have permission for this action.',

  // Validation errors
  'validation|invalid input': 'Please check your input and try again.',
  'must be.*email|invalid.*email': 'Please enter a valid email address.',
  'must be.*number|must be.*integer': 'Please enter a valid number.',
  'min.*length|too short': 'Input is too short.',
  'max.*length|too long': 'Input is too long.',

  // File errors
  'file.*size|file too large': 'The file is too large.',
  'file.*type|invalid.*file|unsupported.*format': 'This file type is not supported.',
  'file.*not found': 'The file could not be found.',

  // Payment errors
  'payment.*failed|payment.*declined': 'Payment could not be processed. Please try again or use a different payment method.',
  'payment.*invalid|invalid.*card': 'Please check your payment information.',
  'insufficient.*funds': 'Insufficient funds. Please check your payment method.',

  // Network errors
  'timeout|request.*timeout': 'Request took too long. Please check your connection and try again.',
  'network.*error|connection.*refused': 'Network error. Please check your connection.',
  'cors|cross-origin': 'This request is not allowed.',
};

/**
 * Sanitize error message based on HTTP status and message content
 * @param {number} status - HTTP status code
 * @param {string} message - Error message from backend
 * @param {object} errors - Validation errors object
 * @returns {object} Sanitized error info
 */
export function sanitizeError(status, message, errors = null) {
  const sanitized = {
    status,
    message: getSafeMessage(status, message),
    userMessage: getSafeUserMessage(status, message),
    isDevMessage: isDevelopment,
  };

  // Include validation errors if provided (these are safe to show)
  if (errors && typeof errors === 'object') {
    sanitized.fieldErrors = sanitizeFieldErrors(errors);
  }

  // In development, also include original message for debugging
  if (isDevelopment) {
    sanitized.originalMessage = message;
  }

  return sanitized;
}

/**
 * Get safe message based on status code and pattern matching
 */
function getSafeMessage(status, message) {
  // If we have a status code mapping, use it
  if (STATUS_CODE_MESSAGES[status]) {
    return STATUS_CODE_MESSAGES[status];
  }

  // Try to find matching pattern
  const lowerMessage = (message || '').toLowerCase();
  for (const [pattern, safeMsg] of Object.entries(ERROR_PATTERN_MAPPING)) {
    const regex = new RegExp(pattern, 'i');
    if (regex.test(lowerMessage)) {
      return safeMsg;
    }
  }

  // Generic fallback based on status code range
  if (status >= 500) {
    return 'A server error occurred. Please try again later.';
  }
  if (status >= 400) {
    return 'Request failed. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Get user-friendly message (slightly different from internal message)
 */
function getSafeUserMessage(status, message) {
  const base = getSafeMessage(status, message);

  // Add helpful context
  if (status === 422) {
    return 'Please review your input and try again.';
  }
  if (status === 401) {
    return 'Please log in to continue.';
  }
  if (status === 403) {
    return 'You do not have access to this action.';
  }

  return base;
}

/**
 * Sanitize validation field errors
 * Keep field-level errors (safe) but make sure messages are clear
 */
function sanitizeFieldErrors(errors) {
  if (!errors || typeof errors !== 'object') {
    return null;
  }

  const sanitized = {};

  for (const [field, messages] of Object.entries(errors)) {
    if (Array.isArray(messages)) {
      // Clean field names for display
      const fieldDisplay = field.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');

      // Keep validation messages as-is (they're user-facing)
      sanitized[field] = messages.map(msg => {
        // Remove duplicate field name if message includes it
        let cleanMsg = msg.replace(new RegExp(`^${field}\\s*`, 'i'), '');
        cleanMsg = cleanMsg.charAt(0).toUpperCase() + cleanMsg.slice(1);
        return cleanMsg;
      });
    } else if (typeof messages === 'string') {
      sanitized[field] = [messages];
    }
  }

  return Object.keys(sanitized).length > 0 ? sanitized : null;
}

/**
 * Format error for display in UI
 * @param {object} error - Error object from API or sanitized error
 * @returns {object} Formatted error for UI
 */
export function formatErrorForUI(error) {
  if (!error) {
    return {
      message: 'An error occurred. Please try again.',
      fieldErrors: null,
    };
  }

  // If already sanitized
  if (error.userMessage) {
    return {
      message: error.userMessage,
      fieldErrors: error.fieldErrors,
    };
  }

  // Otherwise sanitize it
  const sanitized = sanitizeError(
    error.status || 400,
    error.message || 'An error occurred',
    error.errors || error.body?.errors
  );

  return {
    message: sanitized.userMessage,
    fieldErrors: sanitized.fieldErrors,
  };
}

/**
 * Get display message (in current language if available)
 */
export function getDisplayMessage(error, isAr = false) {
  const { message } = formatErrorForUI(error);

  // Could add i18n mapping here in future
  // For now, return English messages
  return message;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error) {
  return error?.status === 422 || (error?.fieldErrors && Object.keys(error.fieldErrors).length > 0);
}

/**
 * Check if error is an auth error
 */
export function isAuthError(error) {
  return error?.status === 401 || error?.status === 403;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error) {
  return error?.status === 0 || !error?.status;
}

export default {
  sanitizeError,
  formatErrorForUI,
  getDisplayMessage,
  isValidationError,
  isAuthError,
  isNetworkError,
};
